import { motion } from "framer-motion";
import { useRef, useState } from "react";

const AboutImpact = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const handleVideoToggle = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-purple-50 min-h-[60vh]">
      <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center gap-14 md:gap-20">
        <motion.div
          className="flex-1 flex justify-center items-center"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div
            className="relative rounded-3xl bg-white/40 backdrop-blur-lg shadow-2xl border border-gray-200 overflow-hidden w-full max-w-xl p-2 md:p-4 group hover:scale-[1.025] hover:shadow-3xl transition-all duration-300"
            style={{ boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.18)" }}
          >
            <div
              className="absolute -inset-1 rounded-3xl pointer-events-none z-0 bg-gradient-to-br from-primary/30 via-secondary/20 to-pink-200 animate-pulse"
              style={{ filter: "blur(16px)", opacity: 0.3 }}
            />
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              className="relative rounded-2xl shadow-xl w-full h-[340px] md:h-[440px] object-cover z-10 border-4 border-white/60"
              aria-label="Impact Together video"
            >
            <source src="/videos/impact-together.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Play/Pause Button Overlay */}
            <button
              aria-label={isPlaying ? "Pause video" : "Play video"}
              onClick={handleVideoToggle}
              className="absolute bottom-6 right-6 z-20 bg-gradient-to-br from-primary to-secondary text-white rounded-full p-3 shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/60 transition-all duration-200"
              tabIndex={0}
            >
              {isPlaying ? (
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5,3 19,12 5,21 5,3" />
                </svg>
              )}
            </button>
          </div>
        </motion.div>

        {/* Right: Text Content in glass card */}
        <motion.div
          className="flex-1 text-left relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          <div className="mb-8">
            <h2 className="text-4xl md:text-4xl font-heading font-extrabold text-primary mb-2 tracking-tight drop-shadow-sm inline-block relative">
              About <span className="text-secondary">Impact Together</span>
              <span className="block h-1 w-2/3 md:w-1/2 bg-gradient-to-r from-primary via-secondary to-pink-400 rounded-full mt-2 animate-pulse" />
            </h2>
          </div>

          {/* Updated Content (replacing the old content) */}
          <div className="overflow-auto h-[250px] md:h-[300px] scrollbar">
            <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-6 font-body">
              At Impact Together, we are committed to driving{" "}
              <span className="text-primary font-semibold">positive social change</span>{" "}
              through{" "}
              <span className="text-primary font-semibold">collaborative efforts</span>{" "}
              and{" "}
              <span className="text-primary font-semibold">innovative solutions</span>
              . With a deep dedication to making a difference, we provide a range of professional services tailored to meet the diverse needs of our clients.
            </p>

            <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-6 font-body">
              Our team specializes in{" "}
              <span className="text-primary font-semibold">research</span>,{" "}
              <span className="text-primary font-semibold">policy analysis</span>,{" "}
              <span className="text-primary font-semibold">strategic planning</span>
              , program design, assessment, training, project auditing, policy
              drafting, and policy design.
            </p>

            <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-6 font-body">
              We firmly believe in the power of partnerships and collaboration to create meaningful impact. Whether working with governments, nonprofits, CSR initiatives, or philanthropists, our goal is to empower our clients with the tools and knowledge they need to effect change in society. Passionate about driving change and making a lasting difference in the world, we strive to deliver impactful results that drive positive change.
            </p>
          </div>

          <motion.blockquote
            className="border-l-4 border-primary pl-4 italic text-secondary text-lg md:text-xl font-medium bg-primary/5 rounded-lg py-3 shadow-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
          >
            “Passionate about driving change and making a lasting difference in the world, we strive to deliver impactful results that drive positive change.”
          </motion.blockquote>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutImpact;
